import { Navigate, Outlet, useLocation } from 'react-router';
import { Settings } from 'lucide-react';
import { EmptyState } from '@helios/ui';
import { DensePagePanel, DensePageShell } from '@/components/layout/DensePageShell';
import { useAuth } from '@/features/auth/authContext';
import { SettingsNav } from '@/features/settings/SettingsNav';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { findDestinationByPath, settingsNavGroupsForPermissions } from '@/navigation/registry';

const accessDenied = (
  <EmptyState
    icon={<Settings className="h-5 w-5" aria-hidden="true" />}
    title="Settings access required"
    description="Your role does not include permission to open organization settings or platform administration."
  />
);

/** `/settings` index: organization settings, or the first section the session may open. */
export function SettingsIndex() {
  const { authenticated, permissions } = useAuth();
  const groups = authenticated ? settingsNavGroupsForPermissions(permissions) : [];
  const items = groups.flatMap((group) => group.items);
  const organization = items.find((item) => item.id === 'settings');
  if (organization) {
    return <SettingsPage />;
  }
  if (items[0]) {
    return <Navigate to={items[0].path} replace />;
  }
  return accessDenied;
}

/**
 * Honest empty state for a `/settings/*` path that is not a registered
 * destination — a stale bookmark or a typo lands here rather than on a blank
 * panel inside working chrome, which reads as a broken page.
 */
export function SettingsUnknownSection() {
  return (
    <EmptyState
      icon={<Settings className="h-5 w-5" aria-hidden="true" />}
      title="This settings section does not exist"
      description="The URL is not a registered Settings destination. Choose a Settings section to continue."
    />
  );
}

/**
 * Shared chrome for every Settings destination under `/settings`.
 *
 * Desktop Settings destinations live in `SettingsRail` (second left panel next
 * to the primary sidebar). This shell keeps a compact mobile list so tablet
 * and phone still reach every section without a hover flyout.
 */
export function SettingsShell() {
  const { authenticated, permissions } = useAuth();
  const { pathname } = useLocation();
  const groups = authenticated ? settingsNavGroupsForPermissions(permissions) : [];
  const destination = findDestinationByPath(pathname.replace(/\/+$/, '') || '/');
  const allocated =
    destination?.id === 'integrations' &&
    groups.some((group) => group.items.some((item) => item.id === destination.id));

  if (groups.length === 0) {
    return accessDenied;
  }

  return (
    <DensePageShell className="min-w-0">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
        <div className="shrink-0 lg:hidden">
          <SettingsNav groups={groups} variant="mobile" />
        </div>
        <DensePagePanel
          data-settings-body=""
          data-settings-page-mode={allocated ? 'allocated' : 'flow'}
          className={
            allocated ? 'min-w-0 overflow-hidden' : 'min-w-0 overflow-y-auto overflow-x-hidden'
          }
        >
          {allocated ? (
            <Outlet />
          ) : (
            <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
              <Outlet />
            </div>
          )}
        </DensePagePanel>
      </div>
    </DensePageShell>
  );
}
